import { useEffect, useState } from 'react'
import { Check, Copy } from 'lucide-react'
import { Highlight } from 'prism-react-renderer'
import Prism from 'prismjs'
import 'prismjs/components/prism-bash'
import 'prismjs/components/prism-json'
import 'prismjs/components/prism-markdown'
import 'prismjs/components/prism-powershell'
import 'prismjs/components/prism-python'
import 'prismjs/components/prism-ruby'
import 'prismjs/components/prism-sql'
import 'prismjs/components/prism-typescript'
import 'prismjs/components/prism-jsx'
import 'prismjs/components/prism-tsx'
import 'prismjs/components/prism-yaml'

interface MarkdownCodeBlockProps {
  code: string
  language?: string
}

const LANGUAGE_LABELS: Record<string, string> = {
  bash: 'Bash',
  css: 'CSS',
  html: 'HTML',
  javascript: 'JavaScript',
  js: 'JavaScript',
  json: 'JSON',
  jsx: 'JSX',
  markdown: 'Markdown',
  md: 'Markdown',
  powershell: 'PowerShell',
  ps1: 'PowerShell',
  python: 'Python',
  py: 'Python',
  ruby: 'Ruby',
  rb: 'Ruby',
  shell: 'Shell',
  sql: 'SQL',
  ts: 'TypeScript',
  tsx: 'TSX',
  typescript: 'TypeScript',
  xml: 'XML',
  yaml: 'YAML',
  yml: 'YAML',
}

export function MarkdownCodeBlock({ code, language }: MarkdownCodeBlockProps) {
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!copied) return

    const timeout = window.setTimeout(() => setCopied(false), 2_000)
    return () => window.clearTimeout(timeout)
  }, [copied])

  const copyCode = async () => {
    await navigator.clipboard.writeText(code)
    setCopied(true)
  }

  const normalizedLanguage = language?.toLowerCase()
  const highlightLanguage = PRISM_LANGUAGE_ALIASES[normalizedLanguage || ''] ||
    normalizedLanguage ||
    'text'
  const label = normalizedLanguage
    ? (LANGUAGE_LABELS[normalizedLanguage] || normalizedLanguage)
    : 'Código'

  return (
    <figure className="my-3 overflow-hidden rounded-xl border border-border bg-card text-foreground shadow-xs">
      <figcaption className="flex h-9 items-center justify-between border-b border-border bg-muted/35 px-3.5">
        <span className="text-[11px] font-medium tracking-wide text-muted-foreground">{label}</span>
        <button
          type="button"
          onClick={() => void copyCode()}
          className="flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
          aria-label={copied ? 'Código copiado' : 'Copiar código'}
        >
          {copied ? (
            <Check aria-hidden="true" className="size-3.5 text-success" />
          ) : (
            <Copy aria-hidden="true" className="size-3.5" />
          )}
          {copied ? 'Copiado' : 'Copiar'}
        </button>
      </figcaption>
      <Highlight prism={Prism} code={code} language={highlightLanguage}>
        {({ tokens, getLineProps, getTokenProps }) => (
          <pre className="agent-code m-0 max-h-[32rem] overflow-auto bg-card px-4 py-3.5 text-[12.5px] leading-[1.65] text-foreground [tab-size:2]">
            <code className={normalizedLanguage ? `language-${normalizedLanguage}` : undefined}>
              {tokens.map((line, lineIndex) => {
                const { style: _lineStyle, ...lineProps } = getLineProps({ line })

                return (
                  <span key={lineIndex} {...lineProps} className="block min-h-[1.65em]">
                    {line.map((token, tokenIndex) => {
                      const { style: _tokenStyle, ...tokenProps } = getTokenProps({ token })
                      return <span key={tokenIndex} {...tokenProps} />
                    })}
                  </span>
                )
              })}
            </code>
          </pre>
        )}
      </Highlight>
    </figure>
  )
}

const PRISM_LANGUAGE_ALIASES: Record<string, string> = {
  html: 'markup',
  md: 'markdown',
  ps1: 'powershell',
  py: 'python',
  rb: 'ruby',
  shell: 'bash',
  ts: 'typescript',
  yml: 'yaml',
}
